import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useToast } from '../hooks/use-toast';

const ConsultationForm = ({ title = "Get in touch with us", buttonText = "Get free consultation", isHeroForm = false }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: 'US',
    amountLost: '$10,000 - $20,000',
    caseOutline: '',
    brokerName: ''
  });
  const [detectedCountry, setDetectedCountry] = useState('us');
  const [userIP, setUserIP] = useState('');
  const [urlParams, setUrlParams] = useState({
    affiliate_encoded_id: '',
    transaction_id: '',
    tid: '',
    sid: '',
    aid: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Extract URL parameters on component mount
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const affiliateId = urlSearchParams.get('affiliate_encoded_id') || '';
    const transactionId = urlSearchParams.get('transaction_id') || '';
    const tid = urlSearchParams.get('tid') || '';
    const sid = urlSearchParams.get('sid') || '';
    const aid = urlSearchParams.get('aid') || '';
    
    setUrlParams({
      affiliate_encoded_id: affiliateId,
      transaction_id: transactionId,
      tid: tid,
      sid: sid,
      aid: aid
    });
  }, []);

  // Auto-detect country and IP based on user's location
  useEffect(() => {
    const detectCountryAndIP = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          setDetectedCountry(data.country_code.toLowerCase());
        }
        if (data.ip) {
          setUserIP(data.ip);
        }
      } catch (error) {
        console.log('Could not detect country and IP, using defaults');
        // Fallback to US if detection fails
        setDetectedCountry('us');
        setUserIP('');
      }
    };
    
    detectCountryAndIP();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validation for required fields
    const requiredFields = isHeroForm 
      ? ['fullName', 'email', 'phone', 'amountLost', 'caseOutline']
      : ['fullName', 'email', 'phone', 'country', 'brokerName', 'amountLost'];
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare payload with IP address and URL parameters
      const payload = {
        ...formData,
        ipAddress: userIP,
        submissionTime: new Date().toISOString(),
        formType: 'single-step',
        // Include URL parameters when available
        ...(urlParams.affiliate_encoded_id && { affiliate_encoded_id: urlParams.affiliate_encoded_id }),
        ...(urlParams.transaction_id && { transaction_id: urlParams.transaction_id }),
        ...(urlParams.tid && { tid: urlParams.tid }),
        ...(urlParams.sid && { sid: urlParams.sid }),
        ...(urlParams.aid && { aid: urlParams.aid })
      };

      // Submit to webhook
      const response = await fetch('https://hook.eu2.make.com/2vgr2hrnl6grsv83b4vofpdf4pblgjjp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({
          title: "Consultation Requested",
          description: "Thank you! We'll contact you within 24 hours for your free evaluation.",
          variant: "default"
        });

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          country: 'US',
          amountLost: '$10,000 - $20,000',
          caseOutline: '',
          brokerName: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "There was an error submitting your form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroAmountOptions = [
    'Less than $2,000',
    '$2,000 - $10,000',
    '$10,000 - $20,000',
    '$20,000 - $50,000',
    '$50,000 or more'
  ];

  if (isHeroForm) {
    return (
      <div className="bg-white p-8 rounded-[20px] shadow-lg max-w-md w-full">
        <h3 className="text-[22px] font-bold text-black mb-6 font-clash">{title}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name and Email side by side */}
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
                placeholder="Full Name"
                required
              />
            </div>
            <div className="flex-1">
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
                placeholder="Email"
                required
              />
            </div>
          </div>

          {/* Phone Input with Country Detection */}
          <div className="phone-input-container">
            <PhoneInput
              country={detectedCountry}
              value={formData.phone}
              onChange={(phone) => handleInputChange('phone', phone)}
              inputProps={{
                required: true,
                placeholder: 'Phone number'
              }}
              enableSearch={true}
              disableSearchIcon={true}
              countryCodeEditable={false}
              specialLabel=""
            />
          </div>

          {/* Amount Lost Dropdown */}
          <div>
            <label className="text-xs font-bold text-black uppercase tracking-wide mb-2 block">
              HOW MUCH DID YOU LOSE? *
            </label>
            <select 
              value={formData.amountLost} 
              onChange={(e) => handleInputChange('amountLost', e.target.value)}
              className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
              required
            >
              {heroAmountOptions.map((amount) => (
                <option key={amount} value={amount}>
                  {amount}
                </option>
              ))}
            </select>
          </div>

          {/* Textarea */}
          <div>
            <textarea
              value={formData.caseOutline}
              onChange={(e) => handleInputChange('caseOutline', e.target.value)}
              className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 min-h-[100px] resize-none outline-none text-base"
              placeholder="Outline your case"
              required
            />
          </div>

          {/* Submit Button */}
          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full text-white font-bold py-4 rounded-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50"
            style={{ 
              backgroundColor: '#FF3D00',
              border: 'none',
              cursor: 'pointer',
              background: '#FF3D00'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#E53500';
                e.target.style.background = '#E53500';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#FF3D00';
                e.target.style.background = '#FF3D00';
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : buttonText}
          </button>
        </form>
      </div>
    );
  }

  // Regular form (non-hero)
  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-900 mb-6 font-clash">{title}</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="text-sm font-medium text-gray-700 block mb-1">
            Full Name *
          </label>
          <input
            id="fullName"
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-base"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-1">
            Email *
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-base"
            placeholder="Enter your email address"
            required
          />
        </div>

        <div>
          <label htmlFor="phone" className="text-sm font-medium text-gray-700 block mb-1">
            Phone *
          </label>
          <div className="phone-input-container-secondary">
            <PhoneInput
              country={detectedCountry}
              value={formData.phone}
              onChange={(phone) => handleInputChange('phone', phone)}
              inputProps={{
                required: true
              }}
              enableSearch={true}
              disableSearchIcon={true}
              countryCodeEditable={false}
              specialLabel=""
            />
          </div>
        </div>

        <div>
          <label htmlFor="country" className="text-sm font-medium text-gray-700 block mb-1">
            Country *
          </label>
          <select 
            value={formData.country} 
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-base"
            required
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="UK">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="brokerName" className="text-sm font-medium text-gray-700 block mb-1">
            Broker Name *
          </label>
          <input
            id="brokerName"
            type="text"
            value={formData.brokerName || ''}
            onChange={(e) => handleInputChange('brokerName', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-base"
            placeholder="Enter the broker's name"
            required
          />
        </div>

        <div>
          <label htmlFor="amountLost" className="text-sm font-medium text-gray-700 block mb-1">
            Amount Lost *
          </label>
          <select 
            value={formData.amountLost} 
            onChange={(e) => handleInputChange('amountLost', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-base"
            required
          >
            <option value="">Select amount range</option>
            <option value="Less than $2,000">Less than $2,000</option>
            <option value="$2,000 - $10,000">$2,000 - $10,000</option>
            <option value="$10,000 - $20,000">$10,000 - $20,000</option>
            <option value="$20,000 - $50,000">$20,000 - $50,000</option>
            <option value="$50,000 or more">$50,000 or more</option>
          </select>
        </div>

        <button 
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 font-semibold rounded-md transition-all duration-200 hover:shadow-lg disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : buttonText}
        </button>
      </form>
    </div>
  );
};

export default ConsultationForm;